// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { getPosts } from '@/modules/cms/client/api/cms.api';
import Board from '@/modules/core/client/components/Board';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';

/**
 * Build a blog list URL from its parts, omitting defaults.
 *
 * A category is a path (`/blog/category/<slug>`), a tag and a page are
 * query parameters, and the two combine.
 *
 * @param {Object} parts
 * @param {Number} [parts.page] - 1-based page
 * @param {String} [parts.tag] - Tag filter
 * @param {String} [parts.category] - Category slug
 * @returns {String}
 */
function blogUrl({ page, tag, category } = {}) {
  const base = category
    ? `/blog/category/${encodeURIComponent(category)}`
    : '/blog';
  const params = new URLSearchParams();
  if (tag) params.set('tag', tag);
  if (page && page > 1) params.set('page', String(page));
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Format a post date in the reader's language.
 *
 * @param {String} value - ISO date
 * @param {String} language - i18n language
 * @returns {String}
 */
function formatDate(value, language) {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleDateString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * The public blog index: newest posts first, filterable by category and by
 * tag, paginated.
 *
 * `page` and `tag` arrive as strings from the route's query parameters,
 * `category` from the `/blog/category/:category` route.
 */
export default function BlogList({ page: pageParam, tag, category }) {
  const { t, i18n } = useTranslation('cms');

  const page = Math.max(1, parseInt(pageParam, 10) || 1);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(false);
    setNotFound(false);

    getPosts({ locale: i18n.language, page, tag, category })
      .then(result => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(err => {
        if (!cancelled) {
          if (err.response && err.response.status === 404) {
            setNotFound(true);
          } else {
            setError(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, tag, category, i18n.language]);

  const currentCategory = data && data.category;

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - ${t('Blog')}`;
    } else {
      document.title = tag ? `${t('Blog')}: ${tag}` : t('Blog');
    }
  }, [tag, currentCategory]);

  const items = (data && data.items) || [];
  const tags = (data && data.tags) || [];
  const categories = (data && data.categories) || [];
  const totalPages = (data && data.pages) || 1;

  const isFiltered = Boolean(tag || category);

  return (
    <>
      <Board names="forestpath" className="blog-header">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 text-center">
              {currentCategory ? (
                <>
                  <p className="blog-post-kicker">
                    <a href="/blog">{t('Blog')}</a>
                  </p>
                  <h2>{currentCategory.name}</h2>
                  {currentCategory.description && (
                    <h3>{currentCategory.description}</h3>
                  )}
                </>
              ) : (
                <h2>{t('Blog')}</h2>
              )}
              {tag && (
                <h3>
                  {t('Posts tagged “{{tag}}”', { tag })}{' '}
                  <a href={blogUrl({ category })} className="blog-tag-clear">
                    {t('All posts')}
                  </a>
                </h3>
              )}
            </div>
          </div>
        </div>
      </Board>

      <section className="container container-spacer blog-list">
        <div className="row">
          <div className="col-xs-12 col-md-8">
            {isLoading && <LoadingIndicator />}

            {!isLoading && notFound && (
              <>
                <h3>{t('Category not found')}</h3>
                <p>
                  <a href="/blog">← {t('All posts')}</a>
                </p>
              </>
            )}

            {!isLoading && error && (
              <div className="alert alert-warning">
                {t('Could not load the blog. Please try again later.')}
              </div>
            )}

            {!isLoading && !error && !notFound && !items.length && (
              <p className="lead text-muted">{t('No posts yet.')}</p>
            )}

            {!isLoading &&
              items.map(post => (
                <article key={post.slug} className="blog-card">
                  {post.coverImage && (
                    <a href={`/blog/${post.slug}`} className="blog-card-cover">
                      <img src={post.coverImage} alt="" />
                    </a>
                  )}
                  {post.category && (
                    <p className="blog-card-category">
                      <a
                        href={blogUrl({ category: post.category.slug })}
                        className="blog-category-link"
                      >
                        {post.category.name}
                      </a>
                    </p>
                  )}
                  <h3 className="blog-card-title">
                    <a href={`/blog/${post.slug}`}>{post.title}</a>
                  </h3>
                  <p className="blog-card-meta text-muted">
                    <time dateTime={post.publishAt}>
                      {formatDate(post.publishAt, i18n.language)}
                    </time>
                    {post.author && post.author.displayName && (
                      <>
                        {' · '}
                        {post.author.displayName}
                      </>
                    )}
                  </p>
                  {post.excerpt && (
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                  )}
                  {post.tags && post.tags.length > 0 && (
                    <p className="blog-card-tags">
                      {post.tags.map(postTag => (
                        <a
                          key={postTag}
                          href={blogUrl({ tag: postTag })}
                          className="label label-default"
                        >
                          {postTag}
                        </a>
                      ))}
                    </p>
                  )}
                  <a href={`/blog/${post.slug}`} className="blog-card-more">
                    {t('Read more')}
                  </a>
                </article>
              ))}

            {!isLoading && totalPages > 1 && (
              <nav className="blog-pagination" aria-label="Blog pages">
                <ul className="pager">
                  <li className={page <= 1 ? 'previous disabled' : 'previous'}>
                    {page > 1 ? (
                      <a href={blogUrl({ page: page - 1, tag, category })}>
                        ← {t('Newer posts')}
                      </a>
                    ) : (
                      <span>← {t('Newer posts')}</span>
                    )}
                  </li>
                  <li className="blog-pagination-status text-muted">
                    {t('Page {{page}} of {{pages}}', {
                      page,
                      pages: totalPages,
                    })}
                  </li>
                  <li className={page >= totalPages ? 'next disabled' : 'next'}>
                    {page < totalPages ? (
                      <a href={blogUrl({ page: page + 1, tag, category })}>
                        {t('Older posts')} →
                      </a>
                    ) : (
                      <span>{t('Older posts')} →</span>
                    )}
                  </li>
                </ul>
              </nav>
            )}
          </div>

          {(categories.length > 0 || tags.length > 0) && (
            <aside className="col-xs-12 col-md-3 col-md-offset-1">
              {categories.length > 0 && (
                <div className="panel panel-default blog-categories">
                  <div className="panel-heading">
                    <h4 className="panel-title">{t('Categories')}</h4>
                  </div>
                  <div className="list-group">
                    <a
                      href={blogUrl()}
                      className={
                        isFiltered
                          ? 'list-group-item'
                          : 'list-group-item active'
                      }
                    >
                      {t('All posts')}
                    </a>
                    {categories.map(item => (
                      <a
                        key={item.slug}
                        href={blogUrl({ category: item.slug })}
                        className={
                          item.slug === category
                            ? 'list-group-item active'
                            : 'list-group-item'
                        }
                      >
                        {item.name}
                        <span className="badge">{item.count}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {tags.length > 0 && (
                <div className="panel panel-default blog-tags">
                  <div className="panel-heading">
                    <h4 className="panel-title">{t('Tags')}</h4>
                  </div>
                  <div className="panel-body blog-tag-cloud">
                    {tags.map(availableTag => (
                      <a
                        key={availableTag}
                        href={blogUrl({ tag: availableTag })}
                        className={
                          availableTag === tag
                            ? 'label label-primary'
                            : 'label label-default'
                        }
                      >
                        {availableTag}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>
      </section>
    </>
  );
}

BlogList.propTypes = {
  page: PropTypes.string,
  tag: PropTypes.string,
  category: PropTypes.string,
};
